import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ComicButton } from './ComicButton';
import { Emoji } from './Emoji';
import { Stamp } from './Stamp';
import { BELOW_SM, useMediaQuery } from './useMediaQuery';
import {
  SCORE_MID,
  SCORE_VALUES,
  indexToScore,
  scoreFace,
  scoreToIndex,
  tileColor,
  tileTextColor,
} from './score-math';
import { cn, formatScore } from './util';

export interface ScoreTilesProps {
  /** Selected score (1…10, 0.5 steps) or null when nothing is chosen yet. */
  value: number | null;
  onChange: (value: number) => void;
  /** Enter / Space on the tiles, or the built-in confirm button. */
  onConfirm?: (value: number) => void;
  /** Render a confirm button under the tiles with this label (e.g. "PUANI VER!"). */
  confirmLabel?: string;
  /** Not interactive (e.g. buyer view, timer expired). */
  disabled?: boolean;
  /** Vote sent: tiles freeze and a "PUAN VERİLDİ!" stamp lands on top. */
  locked?: boolean;
  lockedLabel?: string;
  /** Show the big rotated score badge above the tiles (default true). */
  showBadge?: boolean;
  /** Accessible name of the slider. */
  label?: string;
  className?: string;
}

/** Next score after a key press, or null if the key is not handled. */
export function scoreForKey(key: string, current: number | null): number | null {
  const idx = current == null ? null : scoreToIndex(current);
  const from = idx ?? scoreToIndex(SCORE_MID);
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
      return indexToScore(idx == null ? from : from + 1);
    case 'ArrowLeft':
    case 'ArrowDown':
      return indexToScore(idx == null ? from : from - 1);
    case 'PageUp':
      return indexToScore(from + 2);
    case 'PageDown':
      return indexToScore(from - 2);
    case 'Home':
      return 1;
    case 'End':
      return 10;
    default:
      if (/^[1-9]$/.test(key)) return Number(key);
      if (key === '0') return 10;
      return null;
  }
}

/**
 * The approved jury scorer ("B · Doluluk çubuğu"): 19 tiles 1…10 in 0.5 steps forming a
 * red→yellow→green fill bar. Everything up to the chosen tile is filled, the chosen tile lifts.
 * On phones the bar wraps into two rows (1…5.5 and 6…10) so every tile stays readable and tappable.
 * Keyboard: ←/→ (or ↓/↑) ±0.5, PageUp/PageDown ±1, Home/End, digit keys 1–9 & 0 (=10),
 * Enter/Space confirm.
 */
export function ScoreTiles({
  value,
  onChange,
  onConfirm,
  confirmLabel,
  disabled,
  locked,
  lockedLabel = 'PUAN VERİLDİ!',
  showBadge = true,
  label = 'Jüri puanı',
  className,
}: ScoreTilesProps) {
  const hintId = useId();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  // Two rows on phones: a smaller lift keeps the selected tile (and its arrow) off the row above.
  const narrow = useMediaQuery(BELOW_SM);
  const inert = disabled || locked;
  const selectedIdx = value == null ? -1 : scoreToIndex(value);
  const previewIdx = hover != null && !inert ? hover : -1;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (inert) return;
    if ((e.key === 'Enter' || e.key === ' ') && value != null) {
      e.preventDefault();
      onConfirm?.(value);
      return;
    }
    const next = scoreForKey(e.key, value);
    if (next != null) {
      e.preventDefault();
      if (next !== value) onChange(next);
    }
  };

  return (
    <div className={cn('flex w-full flex-col items-center', className)}>
      {showBadge && (
        <div
          className="relative mb-3 flex h-[60px] items-center justify-center sm:mb-5 sm:h-[76px]"
          aria-hidden
        >
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={value ?? 'none'}
              initial={{ scale: 0.4, rotate: -16, opacity: 0 }}
              animate={{ scale: 1, rotate: -4, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0, transition: { duration: 0.08 } }}
              transition={{ type: 'spring', stiffness: 600, damping: 16 }}
              className={cn(
                'comic flex items-center gap-2 border-4 border-ink px-4 pt-1.5 pb-1 text-[40px] shadow-ink sm:px-5 sm:text-[50px]',
                value == null ? 'bg-white text-ink/40' : 'bg-yellow',
              )}
            >
              {value == null ? '?' : formatScore(value)}
              {value != null && (
                <Emoji emoji={scoreFace(value)} label="" size="0.8em" className="-mt-1" />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      <div className="relative w-full">
        <div
          ref={sliderRef}
          role="slider"
          tabIndex={inert ? -1 : 0}
          aria-label={label}
          aria-valuemin={1}
          aria-valuemax={10}
          aria-valuenow={value ?? undefined}
          aria-valuetext={value == null ? 'Seçilmedi' : `${formatScore(value)} puan`}
          aria-disabled={inert || undefined}
          aria-describedby={hintId}
          onKeyDown={onKeyDown}
          onPointerLeave={() => setHover(null)}
          className={cn(
            'grid w-full grid-cols-10 items-end gap-x-[3px] gap-y-6 px-1 pt-3 pb-1 outline-offset-6',
            'sm:flex sm:justify-center sm:gap-1',
            inert && 'pointer-events-none',
            disabled && !locked && 'opacity-50 grayscale-[0.6]',
          )}
        >
          {SCORE_VALUES.map((score, i) => {
            const isInt = Number.isInteger(score);
            const filled = i <= selectedIdx;
            const ghost = !filled && i <= previewIdx;
            const selected = i === selectedIdx;
            const bg = filled ? tileColor(i) : ghost ? tileColor(i) : '#ffffff';
            return (
              <motion.button
                key={score}
                type="button"
                tabIndex={-1}
                aria-hidden
                onPointerEnter={() => setHover(i)}
                // Keep keyboard focus on the slider so ←/→ + Enter keep working after a click.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (inert) return;
                  onChange(score);
                  sliderRef.current?.focus({ preventScroll: true });
                }}
                animate={
                  selected
                    ? narrow
                      ? { y: -6, scale: 1.12 }
                      : { y: -14, scale: 1.2 }
                    : { y: 0, scale: 1 }
                }
                whileHover={inert || selected ? undefined : { y: -3 }}
                whileTap={inert ? undefined : { scale: 0.92 }}
                transition={{ type: 'spring', stiffness: 700, damping: 22 }}
                className={cn(
                  'relative flex w-full min-w-0 items-end justify-center border-ink pb-1 leading-none font-black tabular-nums',
                  'sm:w-auto sm:max-w-[52px] sm:flex-1',
                  isInt
                    ? 'h-[50px] border-[2.5px] text-[14px] sm:h-[62px] sm:border-3 sm:text-[15px]'
                    : 'h-[42px] border-2 text-[11px] sm:h-[50px]',
                  selected ? 'z-10 shadow-ink-sm' : filled ? '' : 'hover:bg-yellow-soft',
                )}
                style={{
                  backgroundColor: bg,
                  color: filled ? tileTextColor(i) : '#111',
                  opacity: ghost ? 0.55 : 1,
                }}
              >
                {formatScore(score)}
                {selected && (
                  <span className="absolute -top-[13px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-[7px] border-t-[8px] border-x-transparent border-t-ink" />
                )}
              </motion.button>
            );
          })}
        </div>
        <div
          className="mt-1 flex w-full justify-between px-1 text-[12px] font-extrabold text-ink-soft"
          id={hintId}
        >
          <span className="inline-flex items-center gap-1">
            <Emoji emoji="🤮" label="" /> berbat
          </span>
          <span className="hidden sm:inline">← → ile seç · Enter ile onayla</span>
          <span className="inline-flex items-center gap-1">
            efsane <Emoji emoji="👑" label="" />
          </span>
        </div>

        <AnimatePresence>
          {locked && (
            <motion.div
              // Above the lifted (z-10) selected tile so the stamp reads in full.
              className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Stamp tone="green" size="lg" rotate={-6} slam>
                {lockedLabel}
              </Stamp>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {confirmLabel && (
        <ComicButton
          size="lg"
          className="mt-5"
          disabled={value == null || inert}
          disabledReason={
            locked ? 'Puanın gönderildi' : value == null ? 'Önce bir puan seç' : undefined
          }
          onClick={() => value != null && onConfirm?.(value)}
        >
          {confirmLabel}
        </ComicButton>
      )}
    </div>
  );
}
